import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { api } from '../lib/api';
import { money } from '../lib/format';
import { useSession } from '../lib/session';

interface Result {
  id: string;
  title: string;
  subtitle: string;
  badge?: string | null;
  amountMinor?: number;
  currency?: string;
  route: string;
}
interface Group {
  kind: string;
  label: string;
  results: Result[];
}
interface SearchResponse {
  query: string;
  groups: Group[];
}

const GROUP_ICON: Record<string, 'file' | 'users' | 'grid' | 'route' | 'shield'> = {
  documents: 'file',
  counterparties: 'users',
  catalog: 'grid',
  deals: 'route',
  compliance: 'shield',
};

export function Search() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { company } = useSession();
  const q = params.get('q') ?? '';
  const [term, setTerm] = useState(q);
  const [data, setData] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTerm(q);
    if (!q || !company) {
      setData(null);
      return;
    }
    setLoading(true);
    api
      .get<SearchResponse>(`/search?q=${encodeURIComponent(q)}`)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [q, company]);

  const total = data?.groups.reduce((s, g) => s + g.results.length, 0) ?? 0;

  return (
    <main className="main">
      <div className="page-head">
        <div className="page-head__title">
          <span className="eyebrow">Search</span>
          <h1 className="h1">{q ? `Resultados para “${q}”` : 'Buscar'}</h1>
        </div>
      </div>

      <div className="search" style={{ maxWidth: 560, height: 44, marginBottom: 24 }}>
        <Icon name="search" size={18} />
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && term.trim()) setParams({ q: term.trim() });
          }}
          placeholder="Search documents, clients, deals…"
          autoFocus
        />
      </div>

      {loading && <div className="faint" style={{ padding: 20 }}>Buscando…</div>}

      {!loading && q && total === 0 && (
        <div className="card"><div className="empty">No results for "{q}". Try a document number, a name or a code.</div></div>
      )}

      <div className="col gap-20">
        {data?.groups.filter((g) => g.results.length > 0).map((g) => (
          <div className="card" key={g.kind}>
            <div className="card__head">
              <span className="card__title row center gap-8">
                <Icon name={GROUP_ICON[g.kind] ?? 'file'} size={16} className="faint" />
                {g.label}
              </span>
              <span className="tag">{g.results.length}</span>
            </div>
            <div className="card__body card__body--flush">
              {g.results.map((r) => (
                <button key={r.id} className="todo-item" onClick={() => nav(r.route)}>
                  <span className="todo-item__body">
                    <span className="todo-item__title">{r.title}</span>
                    <span className="todo-item__meta">{r.subtitle}</span>
                  </span>
                  {r.badge && <span className="type-tag">{r.badge}</span>}
                  {r.amountMinor != null && r.currency && (
                    <span className="todo-item__amt">{money(r.amountMinor, r.currency)}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}
